import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "src/common/state/hooks";
import ModalStandard from "../../../common/components/Modal/ModalStandard";
import { IsLoading } from "../../../common/components/AppState/IsLoading";
import { Checkbox, Dropdown } from "../../../common/components/Input";
import InfoIconWithTooltip from "../../../common/components/Icon/InfoIconWithTooltip";
import { saveLrsConfig } from "../state/lrsSlice";
import type {
  ILrsConfig,
  ILrsConfigPatch,
  LrsActorMode,
  LrsAnonymousMode,
} from "../state/lrsSlice";
import { lrsRequestErrorMessage } from "../lrsMessages";

export const LRS_DATA_DIALOG_COMPONENT_ID = "lrsDataDialog";

interface LrsDataDialogProps {
  isOpen: boolean;
  config: ILrsConfig | null;
  onClose: () => void;
  onSaved: () => void;
}

type DataValues = {
  actor_mode: LrsActorMode;
  anonymous_mode: LrsAnonymousMode;
  include_navigation: boolean;
};

const FieldLabel = ({ text, hint }: { text: string; hint: string }) => (
  <div className="flex items-center mb-1">
    <label className="block text-sm font-medium text-gray-700">{text}</label>
    <InfoIconWithTooltip message={hint} className="ml-2" />
  </div>
);

const LrsDataDialog = ({ isOpen, config, onClose, onSaved }: LrsDataDialogProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const actorLocked = Boolean(config?.actor_mode_locked_at);

  const { register, handleSubmit, reset } = useForm<DataValues>({
    defaultValues: {
      actor_mode: "pseudonym",
      anonymous_mode: "ignore",
      include_navigation: false,
    },
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    reset({
      actor_mode: config?.actor_mode ?? "pseudonym",
      anonymous_mode: config?.anonymous_mode ?? "ignore",
      include_navigation: config?.include_navigation ?? false,
    });
    setFailure(null);
    setBusy(false);
  }, [isOpen]);

  const onSubmit = async (values: DataValues) => {
    setBusy(true);
    setFailure(null);
    const patch: ILrsConfigPatch = {
      anonymous_mode: values.anonymous_mode,
      include_navigation: values.include_navigation,
    };
    if (!actorLocked) {
      patch.actor_mode = values.actor_mode;
    }
    const saved = await dispatch(
      saveLrsConfig({ componentId: LRS_DATA_DIALOG_COMPONENT_ID, patch }),
    );
    setBusy(false);
    if (!saveLrsConfig.fulfilled.match(saved)) {
      setFailure(lrsRequestErrorMessage(t, saved.payload));
      return;
    }
    onSaved();
  };

  return (
    <ModalStandard
      id="lrs-data-dialog"
      fullScreen={false}
      controlledMode={{ isOpen }}
      onClose={onClose}
      showCloseButton
      minimize
      className="w-full max-w-xl h-fit"
    >
      <div className="completely-centered z-50">
        <IsLoading componentId={LRS_DATA_DIALOG_COMPONENT_ID} showSuccess={false} />
      </div>
      <h2 className="pt-1 pb-3 font-medium text-lg pr-6">
        {t("pages.profile.organization.lrs.dataDialogTitle")}
      </h2>

      <form
        onSubmit={(event) => void handleSubmit(onSubmit)(event)}
        className="flex flex-col gap-3"
      >
        <div>
          <FieldLabel
            text={t("pages.profile.organization.lrs.actorMode")}
            hint={t("pages.profile.organization.lrs.actorModeHint")}
          />
          <Dropdown
            key={`data-actor-${String(isOpen)}`}
            width="w-full"
            options={[
              {
                value: "pseudonym",
                optionText: t("pages.profile.organization.lrs.actorPseudonym"),
              },
              { value: "email", optionText: t("pages.profile.organization.lrs.actorEmail") },
            ]}
            asDefaultValue
            defaultValueIndex={config?.actor_mode === "email" ? 1 : 0}
            disabled={actorLocked}
            registerFct={() => register("actor_mode")}
          />
          <p className={`text-xs text-gray-500 mt-1 ${actorLocked ? "" : "invisible"}`}>
            {t("pages.profile.organization.lrs.actorModeLocked")}
          </p>
        </div>

        <div>
          <FieldLabel
            text={t("pages.profile.organization.lrs.anonymousMode")}
            hint={t("pages.profile.organization.lrs.anonymousHint")}
          />
          <Dropdown
            key={`data-anonymous-${String(isOpen)}`}
            width="w-full"
            options={[
              { value: "ignore", optionText: t("pages.profile.organization.lrs.anonymousIgnore") },
              {
                value: "per_session",
                optionText: t("pages.profile.organization.lrs.anonymousPerSession"),
              },
            ]}
            asDefaultValue
            defaultValueIndex={config?.anonymous_mode === "per_session" ? 1 : 0}
            registerFct={() => register("anonymous_mode")}
          />
        </div>

        <div>
          <FieldLabel
            text={t("pages.profile.organization.lrs.navigation")}
            hint={t("pages.profile.organization.lrs.navigationHint")}
          />
          <Checkbox
            label={t("pages.profile.organization.lrs.navigationLabel")}
            registerFct={() => register("include_navigation")}
          />
        </div>

        <p
          role="alert"
          className={`min-h-[1.5rem] text-sm text-red-600 ${failure ? "" : "invisible"}`}
        >
          {failure ?? "-"}
        </p>

        <div className="flex justify-end items-center gap-3 pt-3 border-t border-gray-200">
          <button type="button" className="btn-alternative-outline text-sm" onClick={onClose}>
            {t("general.cancel")}
          </button>
          <button type="submit" className="btn-primary-fill text-sm" disabled={busy}>
            {t("general.save")}
          </button>
        </div>
      </form>
    </ModalStandard>
  );
};

export default LrsDataDialog;
